import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles, Loader2, ClipboardList, FileSearch, FileWarning, Calculator,
  FileText, MessageSquarePlus, Send, Copy, ScanLine, AlertTriangle, CheckCircle2, Download, Share2, Mail,
} from "lucide-react";

const MODES = [
  { key: "action_plan", label: "Action Plan", icon: ClipboardList },
  { key: "missing_docs", label: "Missing Docs", icon: FileWarning },
  { key: "workpaper", label: "Tax Workpaper", icon: Calculator },
  { key: "return_prep", label: "Return Prep", icon: FileText },
  { key: "draft_update", label: "Draft Client Update", icon: MessageSquarePlus },
];

const money = (v) => (typeof v === "number" ? `$${v.toLocaleString()}` : v);
const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

export const TaxAgent = ({ caseId, clientEmail }) => {
  const [busyMode, setBusyMode] = useState(null);
  const [result, setResult] = useState(null); // {mode, data}
  const [extraction, setExtraction] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    api.get(`/staff/cases/${caseId}/agent/extraction`).then(({ data }) => {
      if (data && data.status && data.status !== "none") setExtraction(data);
      if (data?.status === "scanning") startPolling();
    }).catch(() => {});
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const startPolling = () => {
    setScanning(true);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/staff/cases/${caseId}/agent/extraction`);
        setExtraction(data);
        if (data.status === "done" || data.status === "error") {
          clearInterval(pollRef.current);
          setScanning(false);
          if (data.status === "error") toast.error(data.detail || "Scan failed");
          else toast.success("Documents scanned");
        }
      } catch { clearInterval(pollRef.current); setScanning(false); }
    }, 6000);
  };

  const scanDocs = async () => {
    setScanning(true);
    try {
      await api.post(`/staff/cases/${caseId}/agent/scan-docs`);
      toast.info("Scanning documents…");
      startPolling();
    } catch (e) {
      setScanning(false);
      toast.error(e.response?.data?.detail || "Could not start scan");
    }
  };

  const runMode = async (mode) => {
    setBusyMode(mode);
    setResult(null);
    try {
      const { data } = await api.post(`/staff/cases/${caseId}/agent/run`, { mode });
      setResult({ mode, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Agent could not complete that");
    } finally { setBusyMode(null); }
  };

  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true); setAnswer("");
    try {
      const { data } = await api.post(`/staff/cases/${caseId}/agent/ask`, { question });
      setAnswer(data.answer);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Agent could not answer");
    } finally { setAsking(false); }
  };

  return (
    <div className="bg-[#0F1B14] text-[#E7EFE9] border border-[#1E3A2A] rounded-2xl p-6" data-testid="tax-agent">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-[#1A6B46] flex items-center justify-center"><Sparkles className="h-4 w-4 text-white" /></span>
        <div>
          <h2 className="font-heading text-lg font-semibold text-white">Tax Ops Agent</h2>
          <p className="text-xs text-[#8CA898]">AI copilot · advisory · grounded in this client's data</p>
        </div>
      </div>

      {/* Scan documents */}
      <div className="mt-4 rounded-xl bg-[#132419] border border-[#1E3A2A] p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-medium text-[#E7EFE9] flex items-center gap-2"><ScanLine className="h-4 w-4 text-[#4ADE80]" /> Document scan (reads W-2s, 1099s, prior returns, financials)</span>
          <Button onClick={scanDocs} disabled={scanning} data-testid="agent-scan-btn"
            className="rounded-full bg-[#1A6B46] hover:bg-[#22885A] text-white h-9">
            {scanning ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Scanning…</> : <><FileSearch className="h-4 w-4 mr-2" /> Scan Documents</>}
          </Button>
        </div>
        {extraction?.status === "done" && (
          <div className="mt-3 text-sm" data-testid="agent-extraction">
            {extraction.overall_summary && <p className="text-[#C7D6CC]">{extraction.overall_summary}</p>}
            <div className="mt-2 space-y-2">
              {(extraction.documents || []).map((d, i) => (
                <div key={i} className="rounded-lg bg-[#0F1B14] border border-[#1E3A2A] p-3">
                  <p className="text-[#E7EFE9] font-medium">{d.filename_or_type} {d.tax_year ? `· ${d.tax_year}` : ""}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(d.key_figures || []).map((k, j) => (
                      <span key={j} className="text-xs bg-[#1E3A2A] text-[#9FE7BD] px-2 py-0.5 rounded-full">{k.label}: {k.value}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {extraction.red_flags?.length > 0 && (
              <p className="mt-2 text-xs text-[#FBBF77] flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {extraction.red_flags.join(" · ")}</p>
            )}
            {extraction.skipped_files?.length > 0 && (
              <p className="mt-1 text-[11px] text-[#6B8577]">Skipped (unreadable): {extraction.skipped_files.join(", ")}</p>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="mt-4 flex flex-wrap gap-2">
        {MODES.map((m) => (
          <Button key={m.key} onClick={() => runMode(m.key)} disabled={busyMode !== null}
            data-testid={`agent-${m.key}-btn`} variant="outline"
            className="rounded-full border-[#2C4A38] bg-[#132419] text-[#E7EFE9] hover:bg-[#1A2E20] hover:text-white h-9">
            {busyMode === m.key ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <m.icon className="h-4 w-4 mr-2" />}
            {m.label}
          </Button>
        ))}
      </div>

      {/* Results */}
      {result && (
        <div className="mt-4 rounded-xl bg-[#132419] border border-[#1E3A2A] p-4 text-sm" data-testid="agent-result">
          <AgentResult mode={result.mode} data={result.data} caseId={caseId} clientEmail={clientEmail} />
          {result.data?.disclaimer && <p className="mt-3 text-[11px] text-[#6B8577] italic">{result.data.disclaimer}</p>}
        </div>
      )}

      {/* Ask */}
      <div className="mt-4">
        <div className="flex gap-2">
          <Input value={question} onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()} data-testid="agent-ask-input"
            placeholder="Ask the agent about this client (e.g., is an S-corp worth it here?)"
            className="rounded-full bg-[#132419] border-[#2C4A38] text-white placeholder:text-[#6B8577]" />
          <Button onClick={ask} disabled={asking} data-testid="agent-ask-btn" className="rounded-full bg-[#1A6B46] hover:bg-[#22885A] text-white shrink-0">
            {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        {answer && (
          <div className="mt-3 rounded-xl bg-[#132419] border border-[#1E3A2A] p-4 text-sm text-[#C7D6CC] whitespace-pre-wrap" data-testid="agent-answer">{answer}</div>
        )}
      </div>
    </div>
  );
};

const NotifyBox = ({ checked, onChange, testid, email }) => (
  <div className="flex flex-col gap-0.5">
    <label className="flex items-center gap-1.5 text-[11px] text-[#8CA898] cursor-pointer select-none" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded border border-[#2C4A38] bg-[#132419] accent-[#1A6B46]" />
      <Mail className="h-3 w-3" /> Also email the client
    </label>
    {checked && email && (
      <span className="text-[10px] text-[#6B8577] pl-[22px]" data-testid={`${testid}-preview`}>Email will be sent to {email}</span>
    )}
  </div>
);

const Draft = ({ subject, body, caseId, clientEmail }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [notify, setNotify] = useState(true);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/staff/cases/${caseId}/agent/send-draft`, { subject, body, notify });
      setSent(true);
      toast.success(data?.notified ? "Sent to the client & emailed them" : "Sent to the client's message thread");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send to client");
    } finally { setSending(false); }
  };
  return (
    <div>
      {subject && <p className="text-[#E7EFE9] font-medium">Subject: {subject}</p>}
      <div className="mt-2 rounded-lg bg-[#0F1B14] border border-[#1E3A2A] p-3 text-[#C7D6CC] whitespace-pre-wrap">{body}</div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button onClick={send} disabled={sending || sent} data-testid="agent-send-to-client-btn"
          className="rounded-full bg-[#1A6B46] hover:bg-[#22885A] text-white h-8 text-xs">
          {sending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : sent ? <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
          {sent ? "Sent to client" : "Send to client"}
        </Button>
        <Button onClick={() => copy(`${subject ? subject + "\n\n" : ""}${body}`)} variant="outline"
          className="rounded-full border-[#2C4A38] bg-[#132419] text-[#E7EFE9] h-8 text-xs" data-testid="agent-copy-btn">
          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
        </Button>
        {!sent && <NotifyBox checked={notify} onChange={setNotify} testid="agent-draft-notify-toggle" email={clientEmail} />}
      </div>
    </div>
  );
};

const WorkpaperActions = ({ mode, data, caseId, clientEmail }) => {
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [notify, setNotify] = useState(true);

  const persist = async () => {
    const { data: ref } = await api.post(`/staff/cases/${caseId}/agent/save-pdf`, { mode, data });
    return ref;
  };

  const download = async () => {
    setSaving(true);
    try {
      const ref = await persist();
      const res = await api.get(`/staff/cases/${caseId}/agent/files/${ref.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = ref.original_filename || "workpaper.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Saved to case workpapers & downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save PDF");
    } finally { setSaving(false); }
  };

  const share = async () => {
    setSharing(true);
    try {
      const ref = await persist();
      const { data: res } = await api.post(`/staff/cases/${caseId}/agent/files/${ref.file_id}/share`, { notify });
      setShared(true);
      toast.success(res.notified ? "Shared to the client portal & emailed them" : "Shared to the client portal");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not share to portal");
    } finally { setSharing(false); }
  };

  return (
    <div className="pt-1 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={download} disabled={saving} data-testid="agent-save-pdf-btn" variant="outline"
          className="rounded-full border-[#2C4A38] bg-[#132419] text-[#E7EFE9] hover:bg-[#1A2E20] hover:text-white h-8 text-xs">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
          Save as PDF
        </Button>
        <Button onClick={share} disabled={sharing || shared} data-testid="agent-share-portal-btn"
          className="rounded-full bg-[#1A6B46] hover:bg-[#22885A] text-white h-8 text-xs">
          {sharing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : shared ? <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> : <Share2 className="h-3.5 w-3.5 mr-1.5" />}
          {shared ? "Shared to portal" : "Share to client portal"}
        </Button>
      </div>
      {!shared && <NotifyBox checked={notify} onChange={setNotify} testid="agent-share-notify-toggle" email={clientEmail} />}
    </div>
  );
};

const AgentResult = ({ mode, data, caseId, clientEmail }) => {
  if (mode === "action_plan") {
    return (
      <div>
        <p className="text-[#C7D6CC]">{data.summary}</p>
        <ul className="mt-3 space-y-2">
          {(data.priority_actions || []).map((a, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`mt-0.5 text-[10px] uppercase px-1.5 py-0.5 rounded-full ${a.urgency === "high" ? "bg-[#7F1D1D] text-[#FCA5A5]" : a.urgency === "medium" ? "bg-[#78350F] text-[#FCD34D]" : "bg-[#1E3A2A] text-[#9FE7BD]"}`}>{a.urgency}</span>
              <div><span className="text-[#E7EFE9] font-medium">{a.title}</span> <span className="text-[10px] text-[#6B8577]">({a.owner})</span><p className="text-[#8CA898] text-xs">{a.why}</p></div>
            </li>
          ))}
        </ul>
        {data.risks?.length > 0 && <p className="mt-3 text-xs text-[#FBBF77]">Risks: {data.risks.join(" · ")}</p>}
      </div>
    );
  }
  if (mode === "missing_docs") {
    return (
      <div>
        {data.missing?.length > 0 ? (
          <ul className="space-y-1.5">
            {data.missing.map((m, i) => (
              <li key={i} className="flex items-start gap-2"><FileWarning className="h-4 w-4 text-[#FBBF77] mt-0.5" /><span className="text-[#C7D6CC]"><b className="text-[#E7EFE9]">{m.document}</b> — {m.why_needed}</span></li>
            ))}
          </ul>
        ) : <p className="text-[#9FE7BD] flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> No missing documents.</p>}
        <div className="mt-3"><Draft subject={data.email_subject} body={data.email_body} caseId={caseId} clientEmail={clientEmail} /></div>
      </div>
    );
  }
  if (mode === "workpaper") {
    return (
      <div className="space-y-2 text-[#C7D6CC]">
        <div className="flex flex-wrap gap-2">
          <Stat label="Salary" value={money(data.salary)} />
          <Stat label="Distribution" value={money(data.distribution)} />
          <Stat label="Payroll taxes (est)" value={money(data.payroll_taxes_est)} />
        </div>
        {data.quarterly_estimates?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {data.quarterly_estimates.map((q, i) => <Stat key={i} label={q.quarter} value={money(q.amount)} />)}
          </div>
        )}
        {data.narrative && <p className="text-sm">{data.narrative}</p>}
        {data.basis_notes?.length > 0 && <p className="text-xs"><b>Basis:</b> {data.basis_notes.join(" · ")}</p>}
        {data.aaa_notes?.length > 0 && <p className="text-xs"><b>AAA:</b> {data.aaa_notes.join(" · ")}</p>}
        {data.assumptions?.length > 0 && <p className="text-[11px] text-[#6B8577]">Assumptions: {data.assumptions.join(" · ")}</p>}
        <div className="pt-1"><WorkpaperActions mode="workpaper" data={data} caseId={caseId} clientEmail={clientEmail} /></div>
      </div>
    );
  }
  if (mode === "return_prep") {
    return (
      <div className="space-y-3">
        {data.summary && <p className="text-[#C7D6CC]">{data.summary}</p>}
        {(data.returns || []).map((r, i) => (
          <div key={i} className="rounded-lg bg-[#0F1B14] border border-[#1E3A2A] p-3">
            <p className="text-[#E7EFE9] font-medium">{r.form} <span className="text-xs text-[#6B8577]">— {r.purpose}</span></p>
            <ul className="mt-1 text-xs text-[#C7D6CC] space-y-0.5">
              {(r.key_lines || []).map((l, j) => <li key={j}>• {l.line}: <b>{l.value}</b> <span className="text-[#6B8577]">({l.source})</span></li>)}
            </ul>
            {r.open_items?.length > 0 && <p className="mt-1 text-[11px] text-[#FBBF77]">Open: {r.open_items.join(" · ")}</p>}
          </div>
        ))}
        <div className="pt-1"><WorkpaperActions mode="return_prep" data={data} caseId={caseId} clientEmail={clientEmail} /></div>
      </div>
    );
  }
  if (mode === "draft_update") return <Draft body={data.message} caseId={caseId} clientEmail={clientEmail} />;
  return null;
};

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-[#0F1B14] border border-[#1E3A2A] px-3 py-2">
    <p className="text-[10px] uppercase text-[#6B8577]">{label}</p>
    <p className="text-[#9FE7BD] font-semibold">{value}</p>
  </div>
);
